/* eslint-disable max-lines */
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Toolbar from 'components/Layout/Toolbar';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  ICriticalityMetrics,
  IHistoryMetrics,
  IScoreMetrics,
  IVentureStatusMetrics,
  statusesDashboard,
} from 'interfaces/dashboard';
import { statuses } from 'interfaces/file';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import RxOp from 'rxjs-operators';
import categoryService from 'services/category';
import dashboardService from 'services/dashboard';
import selectedVentureService from 'services/profile';
import ventureService from 'services/venture';

import CardGraphs from '../Component/Card';
import DocsActives from '../Graphs/Actives';
import DocumentCriticality from '../Graphs/Criticality';
import HistoricStatus from '../Graphs/HistoricStatus';
import ScoreVenture from '../Graphs/Score';
import DocumentStatus from '../Graphs/Status';
import EntityStatus from '../Graphs/Statuses';
import SnapshotPicker from '../Component/SnapshotPicker';
import DialogStatusByDay from '../Component/DialogStatusByDay';
import { useSnapshots } from '../context/SnapshotsContext';

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
  },
  wrapperGrid: {
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
    backgroundColor: '#8b7c3f',
    borderRadius: '16px',
  },
}));

export const DashboardCategory = () => {
  const { search } = useLocation();
  const params = useParams<{ categoryId: number; ventureId: number }>();
  const containerToPrint = React.useRef();
  const { getUrlWithSnap, redirectWithSnap } = useSnapshots();

  const [ventureMetrics, setVentureMetrics] = useState<IVentureStatusMetrics>();
  const [critacalityMetrics, setCritacalityMetrics] =
    useState<ICriticalityMetrics[]>();
  const [scoreMetrics, setScoreMetrics] = useState<IScoreMetrics>();
  const [historyMetrics, setHistoryMetrics] = useState<IHistoryMetrics>();

  const [categoryTitle, setCategoryTitle] = useState<string>();
  const [categoryId, setCategoryId] = useState<number>();

  const [ventureTitle, setVentureTitle] = useState<string>();
  const [ventureId, setVentureId] = useState<number>();

  const [isOpenDialogStatusByDay, setIsOpenDialogStatusByDay] =
    useState<boolean>(false);
  const [currentStatusByDay, setCurrentStatusByDay] =
    useState<statusesDashboard>();
  const [date, setDate] = useState<Date | null>(null);

  const handleChangeDate = (date: Date | null) => setDate(date);
  const handleCloseStatusByDay = () => {
    setIsOpenDialogStatusByDay(false);
    setCurrentStatusByDay(undefined);
  };

  const handleOpenStatusByDay = (status: statusesDashboard) => {
    setIsOpenDialogStatusByDay(true);
    setCurrentStatusByDay(status);
  };

  const classes = useStyles();
  useEffect(() => {
    selectedVentureService
      .getSelectedVenture()
      .pipe(RxOp.logError())
      .subscribe((selectedVenture) => {
        setVentureId(selectedVenture.id);
        setVentureTitle(selectedVenture.name);

        ventureService.getById(selectedVenture.id).pipe(RxOp.logError());
      });
  }, []);

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    if (ventureId && params.categoryId) {
      categoryService.getById(params.categoryId).subscribe((category) => {
        setCategoryTitle(category.name);
        setCategoryId(category.id);

        dashboardService
          .getStatus({
            ventureId,
            categoryId: params.categoryId,
            params: queryParams,
          })
          .pipe(RxOp.logError())
          .subscribe((m) => setVentureMetrics(m));

        dashboardService
          .getCriticality({
            ventureId,
            categoryId: params.categoryId,
            params: queryParams,
          })
          .pipe(RxOp.logError())
          .subscribe((m) => setCritacalityMetrics(m));

        dashboardService
          .getScore({
            ventureId,
            categoryId: params.categoryId,
            params: queryParams,
          })
          .pipe(RxOp.logError())
          .subscribe((m) => setScoreMetrics(m));

        dashboardService
          .getHistory({
            ventureId,
            categoryId: params.categoryId,
            params: queryParams,
          })
          .pipe(RxOp.logError())
          .subscribe((m) => setHistoryMetrics(m));
      });
    }
  }, [params.cagoryId, params.categoryId, ventureId, queryParams]);

  const { statusByVenture = null, statusByEntity = null } =
    ventureMetrics || {};
  const totalDocumentsVenture = statusByVenture
    ? statusByVenture.find(
        (i) => i.status === statusesDashboard.ACTIVE_DOCUMENTS
      ).value
    : 0;

  const findStatus = (status: statusesDashboard) => {
    return statusByVenture.find((i) => status === i.status);
  };

  const handleDashboardEntity = (entityId: number) => () => {
    redirectWithSnap(
      `/documentation/dashboard/venture/${params.ventureId}/category/${params.categoryId}/entity/${entityId}`
    );
  };

  const getTotalByEntity = useCallback(
    (entityId: number): number =>
      (statusByEntity && statusByEntity)
        .find((item) => item.entityId === entityId)
        .data.find((item) => item.status === statusesDashboard.ACTIVE_DOCUMENTS)
        .value || 0,
    [statusByEntity]
  );

  return (
    <>
      <Toolbar
        title="Dashboard"
        containerToPrint={containerToPrint}
        breadcrumbs={[
          {
            title: 'Dashboard',
            href: getUrlWithSnap('/documentation/dashboard'),
          },
          {
            title: ventureTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${ventureId}`
            ),
          },
          {
            title: categoryTitle,
            href: getUrlWithSnap(
              `/documentation/dashboard/venture/${ventureId}/category/${categoryId}`
            ),
          },
        ]}
      />
      <DialogStatusByDay
        open={isOpenDialogStatusByDay}
        onClose={handleCloseStatusByDay}
        status={currentStatusByDay}
        date={date}
        ventureId={ventureId}
        categoryId={categoryId}
      />
      <Container innerRef={containerToPrint} style={{ marginTop: '40px' }}>
        <Card>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: 20,
              paddingRight: 20,
            }}
          >
            <div>
              <Typography
                variant="h6"
                color="primary"
                style={{ fontWeight: 700 }}
              >
                {ventureTitle}
              </Typography>
              <Typography variant="body1" color="textPrimary">
                {categoryTitle}
              </Typography>
            </div>

            <div>
              <SnapshotPicker onChangeDate={handleChangeDate} />
            </div>
          </Box>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {!Boolean(
                  ventureMetrics &&
                    critacalityMetrics &&
                    scoreMetrics &&
                    historyMetrics
                ) && <LinearProgress color="secondary" />}
              </Grid>
              <Grid item xs={12} lg={12} xl={12}>
                <Grid container className={classes.wrapperGrid}>
                  {statusByEntity &&
                    statusByEntity.map((entity) => (
                      <Grid
                        item
                        key={`entity-status-graph-${entity.entityId}`}
                        xs={12}
                        sm={6}
                        lg={3}
                        xl={2}
                      >
                        <EntityStatus
                          entityName={entity.entityName}
                          metrics={entity.data.filter((i) =>
                            [
                              ...Object.keys(statuses),
                              statusesDashboard.NOT_UPLOADED,
                            ].includes(i.status)
                          )}
                          total={getTotalByEntity(entity.entityId)}
                          onClick={handleDashboardEntity(entity.entityId)}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={9} xl={10}>
                {statusByVenture && (
                  <CardGraphs bgcolor="#161d42">
                    <Grid container>
                      {statusByVenture
                        .filter(
                          (item) =>
                            item.status !== statusesDashboard.ACTIVE_DOCUMENTS
                        )
                        .map((ds) => (
                          <Grid
                            item
                            key={`document-status-graph-${ds.status}`}
                            xs={12}
                            sm={6}
                            lg={3}
                          >
                            <DocumentStatus
                              metrics={ds}
                              total={totalDocumentsVenture}
                              onClick={() => handleOpenStatusByDay(ds.status)}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </CardGraphs>
                )}
              </Grid>
              <Grid item xs={12} lg={3} xl={2}>
                <Grid container>
                  {statusByVenture &&
                    findStatus(statusesDashboard.ACTIVE_DOCUMENTS) && (
                      <Grid item xs={12} sm={6} lg={12}>
                        <DocsActives
                          metrics={findStatus(
                            statusesDashboard.ACTIVE_DOCUMENTS
                          )}
                        />
                      </Grid>
                    )}
                  <Grid item xs={12} sm={6} lg={12}>
                    <DocumentCriticality metrics={critacalityMetrics || []} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={9} xl={10}>
                <HistoricStatus
                  metrics={historyMetrics ? historyMetrics.statusByMonth : []}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={3} xl={2}>
                <ScoreVenture metrics={scoreMetrics || { max: 0, score: 0 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};
